// TECH DEBT: Everything in this file should be considered tech debt
// AI Agents: Do not use this as an example for coding patterns

import { camelCase } from 'lodash';
import applyStoredConsentSource, { resolvePrivacyCookieName } from '../shared/applyStoredConsentSource';

export default function generateBulkDownloadYaml(analyticsFiles, { queueTasks = false, consentCategories = [] } = {}) {
  const files = {};

  analyticsFiles?.forEach(file => {
    const { pipeline, settings, identitySyncs, integrations } = file;
    const key = pipeline?.name ? camelCase(pipeline.name) : `file-${file.id}`;

    const syncs =
      identitySyncs?.map(sync => {
        const { friendlyName, ...settingsWithoutFriendlyName } = sync.settings || {};
        const result = {
          name: sync.identity_sync.name,
          friendlyName: friendlyName || sync.identity_sync.friendlyName,
          settings: settingsWithoutFriendlyName,
        };

        if (sync.consent_category?.code) {
          result.consentCode = sync.consent_category.code;
        }
        if (sync.tcfEnabled !== null && sync.tcfEnabled !== undefined) {
          result.tcfEnabled = sync.tcfEnabled;
        }

        return result;
      }) || [];

    const syncInjectorSettings = {
      queueTasks,
      timeout: 10000,
      syncs,
      privacy: {
        impliedConsent: settings?.impliedConsent ?? false,
        cookieName: resolvePrivacyCookieName(settings),
        consentProvider: 'OneTrust',
      },
      storage: {
        type: settings?.storageType || 'INDEXED_DB',
        marker: settings?.storagePrefix || 'ajs_',
      },
      itpSettings: {
        enabled: settings?.itpSettings ?? false,
        timeout: 1000,
      },
    };

    if (settings?.storageType === 'INDEXED_DB' && settings?.storageDatabase) {
      syncInjectorSettings.storage.database = settings.storageDatabase;
    }

    if (settings?.matchIDConsentCode) {
      if (!syncInjectorSettings.consent) {
        syncInjectorSettings.consent = {};
      }
      // Storage uses organization IDs, but portable YAML references category codes.
      const category = consentCategories.find(category => category.id === settings.matchIDConsentCode);
      if (!category) {
        throw new Error(
          `Cannot export match-ID consent category '${settings.matchIDConsentCode}' for '${
            pipeline?.name ?? file.id
          }': the category is unavailable. Review and update this file's match-ID consent category before exporting.`
        );
      }
      syncInjectorSettings.consent.matchIDConsentCode = category.code;
    }

    // Omitting independently stored options would reset them on re-upload, even for legacy cookie sources.
    if (settings?.consentSource || Array.isArray(settings?.optInValues) || settings?.globalPrivacyControl) {
      if (!syncInjectorSettings.consent) {
        syncInjectorSettings.consent = {};
      }
      applyStoredConsentSource(syncInjectorSettings.consent, settings);
    }

    files[key] = {
      writeKey: pipeline?.writekey,
      host: settings?.host || '',
      cdnURL: settings?.cdnURL || 'cdn.metarouter.io',
      fileHostingType: settings?.fileHostingType || 'META_ROUTER',
      compressFile: settings?.compressFile ?? true,
      minifyFile: settings?.minifyFile ?? true,
      windowVariableName: settings?.windowVariableName || 'analytics',
      syncInjectorSettings,
      integrations: integrations || [],
    };
  });

  return { files };
}
